"use client";

import { useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { postWithCsrf } from "@/lib/fetch-with-csrf";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { isZakelijkRoutePath } from "./WhatsAppButton";
import { useExclusiveLoginFocus } from "./exclusive-login-focus";
import {
  readStickyCtaVisible,
  subscribeStickyCtaVisible,
  stickyCtaVisibleServerSnapshot,
} from "@/lib/sticky-cta-signal";

// Dennis 29-08-2026 (screenshot review): op de zakelijke landingspagina mag
// maar EEN assistent te zien zijn (Sophie, ZakelijkAssistant.js, gerenderd
// vanuit ZakelijkNlPage.js) -- geen tweede/derde floating widget ernaast.
//
// Codex-review PR #1308 (HIGH, BLOKKEREND): deze gate was tot nu toe alleen
// "exact /zakelijk (nl + favotrip.nl)" -- maar /zakelijk/overzicht (WP6)
// mount ZakelijkAssistant óók (zie dat bestand se page.js), en de globale
// layout.js voegt NewsletterTab overal toe. Twee floating widgets op
// dezelfde route. Fix: dezelfde family-brede routecheck als
// WhatsAppButton.js hergebruiken (`isZakelijkRoutePath`,
// canonicalizeSegmentAnyLocale op het eerste padsegment) i.p.v. een tweede,
// los onderhouden padenlijst hier op te bouwen -- zelfde patroon, zelfde
// bron. Dat verbreedt deze gate ook naar de overige zakelijk-subroutes
// (bedankt/bevestigd/inschrijven/vouchers-op-factuur) waar Sophie niet
// gemount is, maar die dragen hun eigen telefoon/e-mail-contactblok
// (ZAKELIJK_CONTACT, SupportLinks.js) -- het weghalen van deze sitebrede
// nieuwsbrief-pop-up daar verwijdert nooit de enige contactoptie.
const emptyHostSubscribe = () => () => {};

/**
 * Nieuwsbrief sidebar tab + popup — matches old Angular design.
 * Blue tab on right side, opens popup with giraffe image + email form.
 *
 * Dennis 2026-04-23 + Dennis 2026-04-25 sweep: this component used to
 * hardcode Dutch ("Nieuwsbrief", "Op zoek naar inspiratie", "Activeren",
 * "Bekijk privacybeleid", "Vul een geldig e-mailadres in"). Every
 * non-NL TLD therefore leaked Dutch in the sidebar tab + popup. Now
 * pulls every label through getTranslations(locale).
 */
export default function NewsletterTab() {
  const pathname = usePathname();
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  // Hooks must run before any early return (Rules of Hooks) -- see
  // Footer.js' identical onZakelijkSubdomain for the full SSR/hydration
  // rationale (server snapshot false, client snapshot reads the real host).
  const onZakelijkSubdomain = useSyncExternalStore(
    emptyHostSubscribe,
    () => window.location.hostname.startsWith("zakelijk."),
    () => false,
  );
  // WP1 (07-09-2026): on mobile, this tab's floating pill (z-40) renders
  // above the sticky voucher/deal purchase bar (StickyVoucherCTA, z-30) and
  // hides the price once the bar slides in. The pill itself may be hidden
  // (unlike the WhatsApp button, see WhatsAppButton.js's own note on why
  // that one hides instead of moving) — only the mobile pill below is
  // gated on this, the desktop sidebar tab is unaffected.
  const stickyCtaVisible = useSyncExternalStore(
    subscribeStickyCtaVisible,
    readStickyCtaVisible,
    stickyCtaVisibleServerSnapshot,
  );

  // Dealpagina's (/packages/<categorie>/<deal>): op de telefoon zweeft de pil
  // anders over de foto's en de inhoud van de deal (Dennis 26-10-2026). Alleen
  // de mobiele pil; de bureaubladtab en andere pagina's blijven zoals ze zijn.
  const isDealDetailPath = /^\/packages\/[^/]+\/[^/]+/.test(pathname || "");

  const isZakelijkFamilyRoute =
    isZakelijkRoutePath(pathname) || (onZakelijkSubdomain && pathname === "/");
  // Inlogscherm voor Exclusive: geen tweede e-mailveld (exclusive-login-focus.js).
  const exclusiveLogin = useExclusiveLoginFocus();

  // Show on all marketing pages
  const hideOn = ["/booking/", "/stripe-payment", "/thankyou"];
  if (hideOn.some(p => pathname?.startsWith(p)) || isZakelijkFamilyRoute || exclusiveLogin) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError(t("newsletter_tab.invalid_email"));
      return;
    }
    setError("");
    try {
      await postWithCsrf("/api/newsletter", { email });
    } catch {
      // silent fail — still show success
    }
    setSubmitted(true);
  }

  return (
    <>
      {/* Sidebar tab — blue, vertical text (desktop) */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 text-white text-sm font-bold tracking-wider px-3 py-5 rounded-l-lg hover:opacity-90 transition-opacity hidden lg:block"
        style={{
          writingMode: "vertical-rl",
          textOrientation: "mixed",
          backgroundColor: "#0070b8",
          letterSpacing: "1px",
        }}
      >
        {t("newsletter_tab.label")}
      </button>

      {/* Mobile tab — small horizontal bar at bottom-right.
          Dennis 2026-04-28 11:14 EEST: was overlapping with the WhatsApp
          floating button on iPhone. WhatsApp uses
          `calc(1.25rem + env(safe-area-inset-bottom))`, so on devices
          with safe-area > 0 it floats UP, closing the gap to this
          newsletter tab which had a static `bottom-20` (80px). The
          newsletter tab must clear the WhatsApp button + the same
          safe-area inset OR the two stack into each other.
          New positioning: 5.5rem (88px) baseline + safe-area-inset-bottom,
          which leaves a comfortable 30+px gap above the 50px WhatsApp
          button on every viewport. */}
      {!stickyCtaVisible && !isDealDetailPath && (
        <button
          onClick={() => setOpen(!open)}
          className="fixed right-4 z-40 text-white text-xs font-bold px-4 py-2 rounded-full hover:opacity-90 transition-opacity lg:hidden flex items-center gap-1.5 shadow-lg"
          style={{
            backgroundColor: "#0070b8",
            bottom: "calc(5.5rem + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {t("newsletter_tab.label")}
        </button>
      )}

      {/* Popup modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />

          {/* Card */}
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden z-10">
            {/* Close button */}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 z-20 bg-gray-800/60 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-800/80 transition-colors text-sm font-bold"
              aria-label={t("newsletter_tab.close")}
            >
              X
            </button>

            {/* Giraffe image */}
            <div className="relative w-full aspect-[4/3]">
              <Image
                src="/images/newsletter-giraffe.jpg"
                alt={t("newsletter_tab.label")}
                fill
                sizes="(max-width: 448px) 100vw, 448px"
                className="object-cover"
                priority
              />
            </div>

            {/* Content */}
            <div className="p-6" style={{ marginTop: "-60px", position: "relative", zIndex: 10 }}>
              <h2 className="text-2xl font-normal text-gray-900 mb-2 leading-snug">
                {t("newsletter_tab.popup_title")}
              </h2>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                {t("newsletter_tab.popup_desc")}
              </p>

              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-700 font-medium">{t("newsletter_tab.thanks")}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("newsletter_tab.email_placeholder")}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                    style={{ backgroundColor: "#ffffff" }}
                  />
                  {error && (
                    <p className="text-red-500 text-xs mb-2">{error}</p>
                  )}
                  <button
                    type="submit"
                    className="text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity text-sm"
                    style={{ backgroundColor: "#0070b8" }}
                  >
                    {t("newsletter_tab.submit")}
                  </button>
                </form>
              )}

              <Link
                href="/privacy-policy"
                className="text-sm mt-3 inline-block hover:underline"
                style={{ color: "#0070b8" }}
              >
                {t("newsletter_tab.view_privacy")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
